import { MouseEvent, memo, useCallback, useMemo } from 'react';

import {
  DeleteFilled,
  EditFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Modal, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { History } from 'history';
import { useLocation, withRouter } from 'react-router-dom';
import { useTableUrlSync } from '../../../../../hooks/useTableUrlSync';
import './CustomFieldTableList.less';

import { ApiError, handleError } from '../../../../../api/base';
import { useAuth } from '../../../../../hooks/useAuth';
import {
  CustomFieldModel,
  ProductCustomFieldValueType,
} from '../../../../../models/custom-field';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { deleteCustomField } from '../../../../../store/features/customFields/customFieldsSlice';
import {
  UserPermissions,
  capitalizeFirstWord,
  formatDate,
  propsAreEqual,
} from '../../../../../util';
import { DrawerHashRoute } from '../../../../containers/Drawers/types';

import './CustomFieldTableList.less';

const { Text } = Typography;

interface CustomFieldTableListProps {
  customFields: CustomFieldModel[];
  loading: boolean;
  history: History;
}

const CustomFieldTableList = ({
  customFields,
  loading,
  history,
}: CustomFieldTableListProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { totalCount, page, pageSize } = useAppSelector(
    (state) => state.customFields
  );
  const { hasPermission } = useAuth();

  const onError = useCallback((err: ApiError) => {
    message.destroy('deleting-custom-field-message');
    handleError(err);
  }, []);

  const handleDeleteCustomField = useCallback(
    async (customField: CustomFieldModel) => {
      message.loading(
        {
          content: 'Deleting custom field...',
          key: 'deleting-custom-field-message',
        },
        0
      );

      const res: any = await dispatch(deleteCustomField(customField.id!));

      if (res.error) {
        onError({ error: res.payload.error });
      } else {
        message.destroy('deleting-custom-field-message');
        message.success(`Custom field '${customField.name}' deleted.`);
      }
    },
    [dispatch, onError]
  );

  const handleConfirmDeleteCustomField = useCallback(
    (e: MouseEvent, customField: CustomFieldModel) => {
      e.preventDefault();
      Modal.confirm({
        title: `Delete custom field '${customField.name}'?`,
        icon: <ExclamationCircleOutlined />,
        content: 'You will not be able to recover this custom field.',
        okText: 'Delete',
        onOk: () => handleDeleteCustomField(customField),
      });
    },
    [handleDeleteCustomField]
  );

  const getValueTypeColor = (valueType: ProductCustomFieldValueType) => {
    switch (valueType) {
      case 'int':
        return 'red';
      case 'string':
        return 'orange';
      case 'float':
        return 'gold';
      case 'enum':
        return 'green';
      case 'multi':
        return 'blue';
      case 'bool':
        return '#EBF9FC';
    }
  };

  const SORT_FIELD_MAP: Record<string, string> = useMemo(
    () => ({ updated_at: 'updated_at' }),
    []
  );

  const { currentSortField, currentSortOrder, onTableChange } = useTableUrlSync(
    {
      location,
      history,
      page,
      pageSize,
      sortFieldMap: SORT_FIELD_MAP,
    }
  );

  const columns: ColumnsType<CustomFieldModel> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name, _) => {
          return <Text style={{ fontWeight: 'bold' }}>{name}</Text>;
        },
      },
      {
        title: 'Attachment',
        dataIndex: 'type',
        key: 'type',
        render: (type) => <Text code>{capitalizeFirstWord(type)}</Text>,
      },
      {
        title: 'Value Type',
        dataIndex: 'value_type',
        key: 'value_type',
        render: (valueType) => (
          <Tag
            color={getValueTypeColor(valueType)}
            style={{ color: '#003D4C', borderColor: '#003D4C' }}
          >
            {capitalizeFirstWord(valueType)}
          </Tag>
        ),
      },
      {
        title: 'Field Key',
        dataIndex: 'field_key',
        key: 'field_key',
        render: (fieldKey) =>
          !!fieldKey && <Text code>{capitalizeFirstWord(fieldKey)}</Text>,
      },
      {
        title: 'Last updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        render: (date: string) => {
          return <div>{date ? formatDate(date) : null}</div>;
        },
        sorter: true,
        sortOrder: currentSortField === 'updated_at' ? currentSortOrder : null,
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (customField) => (
          <Space direction="horizontal" size="middle">
            {hasPermission(UserPermissions.CustomFieldsEdit) ? (
              <Tooltip title="Edit">
                <EditFilled
                  onClick={() =>
                    history.push({
                      hash: DrawerHashRoute.CustomFieldForm,
                      state: { data: customField },
                    })
                  }
                  className="custom-field-table-list-action-icon"
                />
              </Tooltip>
            ) : null}
            {hasPermission(UserPermissions.CustomFieldsDelete) ? (
              <Tooltip title="Delete">
                <DeleteFilled
                  onClick={(e) =>
                    handleConfirmDeleteCustomField(e, customField)
                  }
                  className="custom-field-table-list-action-icon"
                />
              </Tooltip>
            ) : null}
          </Space>
        ),
      },
    ],
    [
      hasPermission,
      history,
      handleConfirmDeleteCustomField,
      currentSortField,
      currentSortOrder,
    ]
  );

  // pagination is driven by URL via useTableUrlSync

  return (
    <>
      <Table
        loading={loading}
        locale={{ emptyText: <></> }}
        rowKey={(customField) => `${customField.id}-row-key`}
        className="custom-fields-table-list"
        columns={columns}
        sortDirections={['ascend', 'descend', 'ascend']}
        dataSource={customFields}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />
    </>
  );
};

export default withRouter<any, any>(memo(CustomFieldTableList, propsAreEqual));
